// ─────────────────────────────────────────────────────────────────
// Glass-i — Year-End Closing pure helpers (Stage 6E.2).
//
// Pure JS — no React, no AntD, no axios. Designed for node:test.
// ─────────────────────────────────────────────────────────────────

export const CLOSING_STATUS_COLOR = {
  draft:     'default',
  previewed: 'processing',
  closed:    'green',
  reopened:  'gold',
  cancelled: 'red'
};
export const CLOSING_STATUS_LABEL = {
  draft:     'Draft',
  previewed: 'Previewed',
  closed:    'Closed',
  reopened:  'Reopened',
  cancelled: 'Cancelled'
};

export const LINE_ROLE_LABEL = {
  carry_forward:     'Carry forward',
  income_close:      'Income closed',
  expense_close:     'Expense closed',
  retained_earnings: 'Retained earnings',
  balancing:         'Balancing',
  memo:              'Memo'
};

// Tolerance kept in lockstep with the backend constant.
export const CLOSING_DIFFERENCE_TOLERANCE = 0.01;

export function formatRupee(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.00';
  return v.toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

export function netProfitLabel(net) {
  const v = Number(net) || 0;
  if (Math.abs(v) < CLOSING_DIFFERENCE_TOLERANCE) return 'Breakeven';
  return v > 0 ? `Net Profit ₹${formatRupee(v)}`
               : `Net Loss ₹${formatRupee(Math.abs(v))}`;
}

export function netProfitColor(net) {
  const v = Number(net) || 0;
  if (Math.abs(v) < CLOSING_DIFFERENCE_TOLERANCE) return '#64748b';
  return v > 0 ? '#1f7a25' : '#b91c1c';
}

export function isDifferenceWithinTolerance(diff) {
  return Math.abs(Number(diff) || 0) < CLOSING_DIFFERENCE_TOLERANCE;
}

export function canTransitionStatus(fromStatus, toStatus) {
  const allowed = {
    draft:     ['previewed', 'cancelled'],
    previewed: ['draft', 'closed', 'cancelled'],
    closed:    ['reopened'],
    reopened:  ['closed', 'cancelled'],
    cancelled: []
  };
  if (!fromStatus) return false;
  return (allowed[fromStatus] || []).includes(toStatus);
}

export function canDeleteClosing(status) {
  return status === 'draft' || status === 'previewed' || status === 'cancelled';
}

export function canEditClosing(status) {
  return status === 'draft' || status === 'previewed';
}

export function canPreviewClosing(status) {
  return status === 'draft' || status === 'previewed';
}

export function canPostClosing(status, differenceAmount) {
  if (status !== 'previewed') return false;
  return isDifferenceWithinTolerance(differenceAmount);
}

export function canCreateOpeningVoucher(closingStatus, openingVoucher) {
  if (closingStatus !== 'closed') return false;
  if (!openingVoucher) return true;
  return openingVoucher.status === 'cancelled';
}

// ── Payload builders ────────────────────────────────────────────
export function buildClosingPayload(form) {
  if (!form) return {};
  return {
    financialYearLabel: form.financialYearLabel || '',
    fromDate:    form.fromDate || null,
    toDate:      form.toDate || null,
    closingDate: form.closingDate || null,
    retainedEarningsAccountId: form.retainedEarningsAccountId || null,
    closeNotes:  form.closeNotes || null
  };
}

// ── Form validators ─────────────────────────────────────────────
export function findClosingFormErrors(form) {
  const errs = [];
  if (!form) return errs;
  if (!form.financialYearLabel) {
    errs.push(['financialYearLabel', 'Financial year label is required.']);
  } else if (!/^[A-Za-z0-9_/.-]+$/.test(String(form.financialYearLabel).trim())) {
    errs.push(['financialYearLabel',
      'Financial year label may contain letters, digits, _ / . - only.']);
  }
  if (!form.fromDate)    errs.push(['fromDate',    'Start date is required.']);
  if (!form.toDate)      errs.push(['toDate',      'End date is required.']);
  if (!form.closingDate) errs.push(['closingDate', 'Closing date is required.']);
  if (form.fromDate && form.toDate && form.toDate < form.fromDate) {
    errs.push(['toDate', 'End date must be on or after start date.']);
  }
  if (form.toDate && form.closingDate && form.closingDate < form.toDate) {
    errs.push(['closingDate',
      'Closing date cannot be before the financial year end date.']);
  }
  if (!form.retainedEarningsAccountId) {
    errs.push(['retainedEarningsAccountId',
      'Select an Equity ledger for retained earnings or capital transfer.']);
  }
  return errs;
}

// Group preview lines by role for the preview drawer.
export function groupPreviewLines(lines) {
  const buckets = {
    incomeClose:     [],
    expenseClose:    [],
    retainedEarnings: [],
    carryForward:    [],
    other:           []
  };
  if (!Array.isArray(lines)) return buckets;
  for (const ln of lines) {
    if (ln.lineRole === 'income_close')       buckets.incomeClose.push(ln);
    else if (ln.lineRole === 'expense_close') buckets.expenseClose.push(ln);
    else if (ln.lineRole === 'retained_earnings') buckets.retainedEarnings.push(ln);
    else if (ln.lineRole === 'carry_forward') buckets.carryForward.push(ln);
    else                                       buckets.other.push(ln);
  }
  return buckets;
}

export function friendlyYecErrorMessage(err) {
  const body = err && err.response && err.response.data
                && err.response.data.error;
  if (body && body.message) return body.message;
  if (err && err.message)   return err.message;
  return 'Could not complete the year-end closing action. Please try again.';
}
